<script lang="ts">
const STORAGE_KEY = 'airi_consent_v1'
</script>
<script setup lang="ts">
import { useRoute } from 'vue-router'
import { computed, ref } from 'vue'

const route = useRoute()



function hasConsent(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw)
      return false
    const obj = JSON.parse(raw)
    return !!(obj && obj.privacy && obj.terms && obj.age)
  }
  catch {
    return false
  }
}

const visible = ref(!hasConsent())

// Hide the gate on routes that must stay reachable even without consent:
//  - /auth (login/register) so new users can sign in
//  - /privacy, /terms (the legal documents the gate links to)
const isAllowedRoute = computed(() => {
  const p = route.path
  return p === '/privacy' || p === '/terms'
    || p.startsWith('/privacy') || p.startsWith('/terms')
    || p.startsWith('/auth')
})

const show = computed(() => visible.value && !isAllowedRoute.value)

const agreePrivacy = ref(false)
const agreeTerms = ref(false)
const confirmAge = ref(false)

const canEnter = computed(() => agreePrivacy.value && agreeTerms.value && confirmAge.value)

function accept() {
  if (!canEnter.value)
    return
  const payload = {
    privacy: true,
    terms: true,
    age: true,
    ts: Date.now(),
    version: '1.0',
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  // Backward-compatible flag so the legacy /auth age gate stays in sync.
  localStorage.setItem('airi_age_confirmed', '1')
  visible.value = false
}
</script>

<template>
  <div v-if="show" class="consent-overlay">
    <div class="consent-card">
      <div class="consent-seal">
        璃
      </div>
      <h2 class="consent-title">
        进入前须知
      </h2>
      <p class="consent-lead">
        云璃 是一位由 AI 驱动的国风虚拟陪伴者。在开始前，请确认以下事项。本产品仅面向
        <b>18 周岁以上成年用户</b>，未成年人禁止使用。
      </p>

      <label class="consent-row" :class="{ on: agreePrivacy }">
        <input v-model="agreePrivacy" type="checkbox">
        <span>
          我已阅读并同意
          <a href="/app/privacy" target="_blank" rel="noopener">《隐私政策》</a>
        </span>
      </label>

      <label class="consent-row" :class="{ on: agreeTerms }">
        <input v-model="agreeTerms" type="checkbox">
        <span>
          我已阅读并同意
          <a href="/app/terms" target="_blank" rel="noopener">《服务协议》</a>
        </span>
      </label>

      <label class="consent-row" :class="{ on: confirmAge }">
        <input v-model="confirmAge" type="checkbox">
        <span>我已满 18 周岁，并确认本人为成年人</span>
      </label>

      <button class="consent-btn" :disabled="!canEnter" @click="accept">
        我已阅读并同意，进入
      </button>

      <p class="consent-foot">
        继续使用即表示你理解 AI 生成内容可能存在偏差，云璃 的输出不构成专业建议。
      </p>
    </div>
  </div>
</template>

<style scoped>
.consent-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(28, 24, 22, 0.82);
  backdrop-filter: blur(6px);
  font-family: 'Noto Serif SC', 'Songti SC', ui-serif, serif;
}

.consent-card {
  width: 100%;
  max-width: 400px;
  background: #fbf7ec;
  border: 1px solid rgba(192, 57, 43, 0.25);
  border-radius: 16px;
  padding: 30px 28px 26px;
  text-align: center;
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.35);
}

.consent-seal {
  width: 48px;
  height: 48px;
  margin: 0 auto 14px;
  border-radius: 50%;
  background: #c0392b;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}

.consent-title {
  font-size: 20px;
  color: #1c1816;
  margin: 0 0 12px;
  letter-spacing: 0.08em;
}

.consent-lead {
  font-size: 13.5px;
  line-height: 1.75;
  color: #5b534a;
  margin: 0 0 18px;
  text-align: left;
}

.consent-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 11px 12px;
  margin-bottom: 10px;
  border: 1px solid rgba(192, 57, 43, 0.12);
  border-radius: 10px;
  background: #fff;
  text-align: left;
  cursor: pointer;
  font-size: 13.5px;
  color: #3a342e;
  transition: all 0.2s ease;
}

.consent-row.on {
  border-color: rgba(192, 57, 43, 0.45);
  background: #fdf3ef;
}

.consent-row input {
  margin-top: 2px;
  width: 16px;
  height: 16px;
  accent-color: #c0392b;
  flex-shrink: 0;
}

.consent-row a {
  color: #c0392b;
  text-decoration: underline;
}

.consent-btn {
  display: block;
  width: 100%;
  padding: 13px;
  margin-top: 8px;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-family: inherit;
  letter-spacing: 0.06em;
  background: #c0392b;
  color: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
}

.consent-btn:disabled {
  background: #d8cdbb;
  color: #fff;
  cursor: not-allowed;
}

.consent-btn:not(:disabled):hover {
  background: #9d2933;
}

.consent-foot {
  font-size: 11.5px;
  line-height: 1.6;
  color: #8a7d6e;
  margin: 14px 0 0;
}
</style>
